import { createApi } from '@reduxjs/toolkit/query/react'

import { IForm } from 'core-components/classes/models/IForm'
import { NotificationController } from 'core-components/library/popup'
import { IUser } from 'core-components/sitebuilder/models/IUser'
import { setUser } from 'core-components/sitebuilder/store/siteBuilderSlice'
import { IDataProviderFilter, IPaging, IPagingFromTo, Logic, Operator } from 'core-components/types'
import { clear, readFromCache, saveToCache } from 'src/core/classes/cache/Cache'
import StorageKeys from 'src/core/classes/cache/StorageKeys'
import { toSameOriginUrl } from 'src/core/classes/helpers/authorization'
import { isCoreServiceAvailable } from 'src/core/classes/helpers/isCoreServiceAvailable'
import {
  clearAccountData,
  clearTokens,
  getActiveTokens,
} from 'src/core/classes/helpers/StorageHelper'
import { proxyIdentity } from 'src/core/classes/helpers/UrlHelper'
import { Rest } from 'src/core/classes/http'
import { IApiUser, map as mapUser } from 'src/core/classes/models/User'
import { fetchHpidSignOutUrl } from 'src/core/classes/services/auth/hpidV3'
import { authService } from 'src/core/classes/services/AuthService'
import { isHPUrl } from 'src/core/layout/login/helpers/isHPUrl'
import { setUser as setCoreUser } from 'src/core/store/appSlice'
import { composeURL } from 'src/core/utils/functions'
import Logger from 'src/core/utils/Logger'
import { isTokenExpired } from 'src/core/utils/Token'

import { getCustomQuery } from '../helpers/getCustomQuery'

export const userApi = createApi({
  reducerPath: '@app/api/userApi',
  baseQuery: getCustomQuery({
    storageKey: StorageKeys.user,
    baseUrl: proxyIdentity('/mbe/api'),
    cachedUrl: [{ url: '/user/account', method: 'GET' }],
  }),
  endpoints: (builder) => ({
    fetchSimulationUsers: builder.query<
      any,
      { filters: IDataProviderFilter; paging: IPaging | IPagingFromTo }
    >({
      query: ({ filters, paging }) => {
        const page = (paging as IPaging).page + 1
        const pageSize = (paging as IPaging).pageSize
        const payload = { Filter: filters, PageSize: pageSize, Page: page }

        return {
          url: '/v3/app/users/for/simulations',
          method: 'POST',
          body: payload,
        }
      },
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (err: unknown) {
          NotificationController.showError({
            message: (err as Error).message,
          })
        }
      },
    }),

    fetchUserRoleIds: builder.query<string[], string>({
      query: (userId: string) => `/v3/users/${userId}/app/roles`,
      transformResponse: (response: any[]) => {
        return response.map((r) => r.RoleId)
      },
    }),
    fetchUser: builder.query<IUser, void>({
      query: () => '/v2/user/account',
      async onQueryStarted(_, { queryFulfilled, dispatch }) {
        const data = readFromCache(StorageKeys.user)
        if (data) {
          dispatch(setUser(data))
          dispatch(setCoreUser(data))
          return
        }

        try {
          const { data: user } = await queryFulfilled
          if (!user) {
            return
          }

          if (user?.UID) {
            const roleIds = await dispatch(
              userApi.endpoints.fetchUserRoleIds.initiate(user.UID)
            ).unwrap()
            const preparedUser = { ...user, roleIds }
            await saveToCache(StorageKeys.user, preparedUser, true)

            dispatch(setUser(preparedUser))
            dispatch(setCoreUser(preparedUser))
          }
        } catch (error) {
          Logger.error(error)
        }
      },
      transformResponse: (response: IApiUser | IUser) => {
        const needPrepareData = (response as IApiUser).Id
        return needPrepareData ? mapUser(response as IApiUser) : (response as IUser)
      },
    }),

    logout: builder.mutation<void, { logoutFromHp?: boolean; returnUrl?: string } | void>({
      queryFn: async (param) => {
        // returnUrl lets a caller send the user back to the page they were on after
        // logout (incl. the HP sign-out round-trip). Defaults to '/' as before.
        const returnUrl = param?.returnUrl ?? '/'
        // signout-url needs a valid bearer token. Nothing refreshes it while the tab has no focus,
        // so a user who sat on a static page past its lifetime would get a local-only sign-out.
        if (param?.logoutFromHp && isTokenExpired(getActiveTokens()[0] || '')) {
          await authService.refreshToken()
        }
        const [accessToken] = getActiveTokens()
        // Needs the bearer token, so it goes before the token is deleted below
        const signOutUrl =
          param?.logoutFromHp && accessToken
            ? await fetchHpidSignOutUrl(accessToken, returnUrl)
            : null

        try {
          await Rest.delete({ url: proxyIdentity('/mbe/api/v2/access/token'), identityToken: true })
        } catch (err: unknown) {
          Logger.error(proxyIdentity('/mbe/api/v2/access/token'), `: `, err)
        }

        clearTokens()
        clearAccountData()
        clear()

        if (signOutUrl && isHPUrl(signOutUrl)) {
          window.location.replace(signOutUrl)
        } else {
          // returnUrl comes from callers; the same rule as every other redirect
          window.location.href = toSameOriginUrl(returnUrl)?.href ?? composeURL('/')
        }

        return { data: undefined }
      },
    }),
    fetchAppUserById: builder.query<IUser | null, string>({
      query: (id: string) => {
        const payload: IForm = {
          PageSize: 1,
          Page: 1,
        }

        if (isCoreServiceAvailable()) {
          payload.Filter = {
            logic: Logic.AND,
            filters: [{ field: 'id', value: id, operator: Operator.EQUAL }],
          }
        } else {
          payload.Filter = { field: 'Id', value: id, operator: Operator.EQUAL }
          payload.Sort = {}
        }

        return {
          url: '/v3/app/users/search',
          method: 'POST',
          body: payload,
        }
      },
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (err: unknown) {
          Logger.error(err)
          NotificationController.showError({
            message: `Something went wrong during the user search. Please try again.`,
          })
        }
      },
      transformResponse: (response: { Data?: IApiUser[] }): IUser | null => {
        const rows = response?.Data || []
        if (!rows.length) return null
        return mapUser(rows[0])
      },
    }),
    downloadUsersList: builder.mutation<Blob, void>({
      queryFn: async () => {
        try {
          const blob = await Rest.post({
            url: proxyIdentity('/mbe/api/v2/export/users'),
            identityToken: true,
          })

          return { data: blob }
        } catch (err: unknown) {
          NotificationController.showError({
            message: (err as Error).message,
          })
          return { error: err as any }
        }
      },
    }),
  }),
})

export const {
  useFetchUserQuery,
  useLogoutMutation,
  useLazyFetchSimulationUsersQuery,
  useDownloadUsersListMutation,
  useLazyFetchAppUserByIdQuery,
} = userApi
